// src/services/fishIQRegionDetector.ts
// Global-ish FishIQ region detector.
// Purpose: choose one or more coarse regional intelligence packs from coordinates,
// water type, and text hints. This file does NOT call network APIs and does NOT
// expose private catch locations.

import {
  classifyWaterContext,
  isNearFishIQCoastlineByCoords,
  type FishIQWaterContextClass,
} from './fishIQWaterContext';

export type FishIQRegionId =
  | 'southern_california'
  | 'california_coast'
  | 'pacific_northwest'
  | 'mountain_west'
  | 'midwest_inland'
  | 'southeast_inland'
  | 'southeast_atlantic'
  | 'florida_inshore'
  | 'gulf_coast'
  | 'great_lakes'
  | 'northeast_us'
  | 'texas_reservoirs'
  | 'southwest_desert'
  | 'alaska'
  | 'hawaii'
  | 'uk_ireland_coarse'
  | 'japan_inshore'
  | 'korea_coastal'
  | 'australia_estuary'
  | 'vietnam'
  | 'singapore'
  | 'malaysia'
  | 'thailand'
  | 'philippines'
  | 'indonesia'
  | 'india_inland'
  | 'india_coastal'
  | 'generic_freshwater'
  | 'generic_saltwater';

export type FishIQRegionDetectionContext = {
  lat?: number | null;
  lon?: number | null;
  waterName?: string | null;
  waterKind?: string | null;
  waterType?: string | null;
  regionHint?: string | null;
};

export type FishIQRegionDefinition = {
  id: FishIQRegionId;
  label: string;
  reasonLabel: string;
  bounds?: { minLat: number; maxLat: number; minLon: number; maxLon: number };
  hints: string[];
  waterTypes?: string[];
  priority: number;
};

const clean = (value: any) => String(value ?? '').trim();
const lower = (value: any) => clean(value).toLowerCase();

export { type FishIQWaterContextClass } from './fishIQWaterContext';

export function classifyFishIQWaterContext(ctx: FishIQRegionDetectionContext): FishIQWaterContextClass {
  return classifyWaterContext(ctx);
}

const COASTAL_REGION_IDS = new Set<FishIQRegionId>([
  'california_coast',
  'southeast_atlantic',
  'florida_inshore',
  'gulf_coast',
  'hawaii',
  'korea_coastal',
  'japan_inshore',
  'australia_estuary',
  'india_coastal',
  'generic_saltwater',
]);

const isCoastalRegion = (id: FishIQRegionId) => COASTAL_REGION_IDS.has(id);

export function isNearFishIQCoastline(ctx: FishIQRegionDetectionContext): boolean {
  return isNearFishIQCoastlineByCoords(ctx);
}

const unique = <T,>(items: T[], limit = 12): T[] => {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items || []) {
    const key = lower(
      typeof item === 'object' && item
        ? (item as any).id || (item as any).label || JSON.stringify(item)
        : item as any,
    );
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
    if (out.length >= limit) break;
  }
  return out;
};

const inBounds = (ctx: FishIQRegionDetectionContext, def: FishIQRegionDefinition) => {
  if (!def.bounds) return false;
  const lat = Number(ctx.lat);
  const lon = Number(ctx.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  return lat >= def.bounds.minLat && lat <= def.bounds.maxLat && lon >= def.bounds.minLon && lon <= def.bounds.maxLon;
};

const textHaystack = (ctx: FishIQRegionDetectionContext) =>
  [ctx.regionHint, ctx.waterName, ctx.waterKind, ctx.waterType]
    .map(lower)
    .filter(Boolean)
    .join(' ');

const hintMatches = (ctx: FishIQRegionDetectionContext, def: FishIQRegionDefinition) => {
  const hay = textHaystack(ctx);
  if (!hay) return false;
  return def.hints.some((hint) => {
    const h = lower(hint);
    return h && (hay.includes(h) || h.includes(hay));
  });
};

const waterTypeFits = (ctx: FishIQRegionDetectionContext, def: FishIQRegionDefinition) => {
  if (!def.waterTypes?.length) return true;
  const water = lower(ctx.waterType || ctx.waterKind || ctx.waterName);
  if (!water) return true;
  return def.waterTypes.some((w) => water.includes(lower(w)) || lower(w).includes(water));
};

export const FISHIQ_REGION_DEFINITIONS: FishIQRegionDefinition[] = [
  {
    id: 'southern_california',
    label: 'Southern California',
    reasonLabel: 'Southern California regional match',
    bounds: { minLat: 32.0, maxLat: 35.95, minLon: -121.0, maxLon: -114.0 },
    hints: ['southern california', 'socal', 'los angeles', 'orange county', 'san diego', 'ventura', 'inland empire'],
    priority: 100,
  },
  {
    id: 'california_coast',
    label: 'California Coast',
    reasonLabel: 'California coast regional match',
    bounds: { minLat: 35.4, maxLat: 42.2, minLon: -124.8, maxLon: -118.0 },
    hints: ['california coast', 'central coast', 'monterey bay', 'san francisco bay', 'bodega bay', 'morro bay'],
    waterTypes: ['bay', 'estuary', 'inshore', 'ocean', 'surf'],
    priority: 96,
  },
  {
    id: 'pacific_northwest',
    label: 'Pacific Northwest',
    reasonLabel: 'Pacific Northwest regional match',
    bounds: { minLat: 42.0, maxLat: 49.2, minLon: -125.5, maxLon: -116.0 },
    hints: ['pacific northwest', 'pnw', 'washington', 'oregon', 'columbia river', 'puget sound', 'willamette'],
    priority: 90,
  },
  {
    id: 'florida_inshore',
    label: 'Florida inshore',
    reasonLabel: 'Florida regional match',
    bounds: { minLat: 24.0, maxLat: 31.2, minLon: -87.8, maxLon: -79.6 },
    hints: ['florida', 'keys', 'everglades', 'tampa bay', 'miami', 'jacksonville', 'fort myers'],
    // Florida has both a coastal pack and a curated freshwater pack. Keep the
    // same regional identity authoritative for lakes/reservoirs/rivers/ponds so
    // South Florida freshwater species do not fall through to broad Southeast
    // or generic freshwater catalogs.
    waterTypes: ['bay', 'estuary', 'inshore', 'marsh', 'canal', 'ocean', 'surf', 'lake', 'reservoir', 'pond', 'river', 'creek'],
    priority: 88,
  },
  {
    id: 'gulf_coast',
    label: 'Gulf Coast',
    reasonLabel: 'Gulf Coast regional match',
    bounds: { minLat: 25.0, maxLat: 31.6, minLon: -98.8, maxLon: -81.0 },
    hints: ['gulf coast', 'gulf of mexico', 'texas coast', 'louisiana', 'mississippi coast', 'alabama coast'],
    waterTypes: ['bay', 'estuary', 'inshore', 'marsh', 'ocean', 'surf'],
    priority: 82,
  },
  {
    id: 'great_lakes',
    label: 'Great Lakes',
    reasonLabel: 'Great Lakes regional match',
    bounds: { minLat: 41.0, maxLat: 49.2, minLon: -93.5, maxLon: -74.0 },
    hints: ['great lakes', 'lake superior', 'lake michigan', 'lake huron', 'lake erie', 'lake ontario'],
    waterTypes: ['lake', 'river', 'reservoir', 'bay'],
    priority: 78,
  },
  {
    id: 'northeast_us',
    label: 'Northeast US',
    reasonLabel: 'Northeast regional match',
    bounds: { minLat: 38.5, maxLat: 47.8, minLon: -80.5, maxLon: -66.0 },
    hints: ['northeast', 'new england', 'maine', 'massachusetts', 'new york', 'new jersey', 'connecticut', 'rhode island'],
    priority: 72,
  },
  {
    id: 'texas_reservoirs',
    label: 'Texas reservoirs',
    reasonLabel: 'Texas regional match',
    bounds: { minLat: 25.5, maxLat: 36.7, minLon: -106.8, maxLon: -93.3 },
    hints: ['texas', 'tx', 'sam rayburn', 'fork', 'toledo bend', 'lake texoma', 'trinity river'],
    waterTypes: ['lake', 'reservoir', 'river', 'pond', 'canal'],
    priority: 70,
  },
  {
    id: 'mountain_west',
    label: 'Mountain West',
    reasonLabel: 'Mountain West regional match',
    bounds: { minLat: 31.0, maxLat: 49.3, minLon: -116.5, maxLon: -102.0 },
    hints: ['mountain west', 'rocky mountains', 'colorado', 'utah', 'wyoming', 'montana', 'idaho', 'new mexico'],
    waterTypes: ['lake', 'reservoir', 'river', 'creek', 'pond'],
    priority: 64,
  },
  {
    id: 'midwest_inland',
    label: 'Midwest inland',
    reasonLabel: 'Midwest inland regional match',
    bounds: { minLat: 36.0, maxLat: 49.3, minLon: -104.2, maxLon: -82.0 },
    hints: ['midwest', 'iowa', 'missouri', 'kansas', 'nebraska', 'dakota', 'minnesota', 'wisconsin', 'illinois', 'indiana', 'ohio'],
    waterTypes: ['lake', 'reservoir', 'river', 'creek', 'pond', 'canal'],
    priority: 63,
  },
  {
    id: 'southeast_inland',
    label: 'Southeast inland',
    reasonLabel: 'Southeast inland regional match',
    bounds: { minLat: 29.0, maxLat: 37.2, minLon: -94.5, maxLon: -75.0 },
    hints: ['southeast', 'georgia', 'alabama', 'tennessee', 'south carolina', 'north carolina', 'arkansas', 'mississippi inland'],
    waterTypes: ['lake', 'reservoir', 'river', 'creek', 'pond', 'canal', 'marsh'],
    priority: 62,
  },
  {
    id: 'southeast_atlantic',
    label: 'Southeast Atlantic',
    reasonLabel: 'Southeast Atlantic regional match',
    bounds: { minLat: 30.0, maxLat: 36.8, minLon: -82.2, maxLon: -75.0 },
    hints: ['southeast atlantic', 'carolina coast', 'georgia coast', 'outer banks', 'charleston', 'savannah'],
    waterTypes: ['bay', 'estuary', 'inshore', 'marsh', 'ocean', 'surf'],
    priority: 81,
  },
  {
    id: 'southwest_desert',
    label: 'Southwest reservoirs',
    reasonLabel: 'Southwest reservoir regional match',
    bounds: { minLat: 31.0, maxLat: 38.5, minLon: -118.8, maxLon: -106.0 },
    hints: ['southwest', 'desert reservoirs', 'arizona', 'nevada', 'lake mead', 'lake powell', 'colorado river'],
    waterTypes: ['lake', 'reservoir', 'river', 'canal', 'pond'],
    priority: 61,
  },
  {
    id: 'alaska',
    label: 'Alaska',
    reasonLabel: 'Alaska regional match',
    bounds: { minLat: 51.0, maxLat: 72.5, minLon: -170.5, maxLon: -129.0 },
    hints: ['alaska', 'anchorage', 'kenai', 'bristol bay', 'inside passage'],
    waterTypes: ['river', 'creek', 'lake', 'bay', 'inshore', 'ocean', 'surf'],
    priority: 76,
  },
  {
    id: 'hawaii',
    label: 'Hawaii',
    reasonLabel: 'Hawaii regional match',
    bounds: { minLat: 18.5, maxLat: 22.8, minLon: -161.0, maxLon: -154.5 },
    hints: ['hawaii', 'oahu', 'maui', 'kauai', 'big island', 'kona', 'honolulu'],
    waterTypes: ['bay', 'inshore', 'ocean', 'surf'],
    priority: 76,
  },
  {
    id: 'uk_ireland_coarse',
    label: 'UK/Ireland coarse waters',
    reasonLabel: 'UK/Ireland regional match',
    bounds: { minLat: 49.5, maxLat: 61.2, minLon: -10.8, maxLon: 2.5 },
    hints: ['united kingdom', 'uk', 'england', 'scotland', 'wales', 'ireland', 'coarse fishing'],
    waterTypes: ['lake', 'pond', 'river', 'canal', 'reservoir'],
    priority: 66,
  },
  {
    id: 'vietnam',
    label: 'Vietnam',
    reasonLabel: 'Vietnam regional match',
    bounds: { minLat: 8.0, maxLat: 23.7, minLon: 102.0, maxLon: 109.8 },
    hints: ['vietnam', 'viet nam', 'mekong delta', 'ho chi minh', 'saigon', 'da nang', 'ha long', 'hanoi', 'nha trang'],
    priority: 78,
  },
  {
    id: 'singapore',
    label: 'Singapore',
    reasonLabel: 'Singapore regional match',
    bounds: { minLat: 1.12, maxLat: 1.55, minLon: 103.55, maxLon: 104.15 },
    hints: ['singapore', 'sentosa', 'pulau ubin', 'marina bay', 'strait of singapore'],
    priority: 79,
  },
  {
    id: 'malaysia',
    label: 'Malaysia',
    reasonLabel: 'Malaysia regional match',
    bounds: { minLat: 0.8, maxLat: 7.8, minLon: 99.0, maxLon: 119.5 },
    hints: ['malaysia', 'malay', 'penang', 'langkawi', 'johor', 'kuala lumpur', 'sabah', 'sarawak', 'borneo'],
    priority: 77,
  },
  {
    id: 'thailand',
    label: 'Thailand',
    reasonLabel: 'Thailand regional match',
    bounds: { minLat: 5.4, maxLat: 20.6, minLon: 97.0, maxLon: 106.2 },
    hints: ['thailand', 'thai', 'bangkok', 'phuket', 'krabi', 'pattaya', 'chiang mai', 'mekong', 'chao phraya'],
    priority: 74,
  },
  {
    id: 'philippines',
    label: 'Philippines',
    reasonLabel: 'Philippines regional match',
    bounds: { minLat: 4.0, maxLat: 21.5, minLon: 116.0, maxLon: 127.5 },
    hints: ['philippines', 'philippine', 'luzon', 'visayas', 'mindanao', 'manila', 'cebu', 'palawan', 'davao'],
    priority: 74,
  },
  {
    id: 'indonesia',
    label: 'Indonesia',
    reasonLabel: 'Indonesia regional match',
    bounds: { minLat: -11.5, maxLat: 6.5, minLon: 94.0, maxLon: 142.5 },
    hints: ['indonesia', 'bali', 'java', 'sumatra', 'borneo', 'kalimantan', 'sulawesi', 'papua', 'jakarta'],
    priority: 74,
  },
  {
    id: 'india_inland',
    label: 'India inland waters',
    reasonLabel: 'India inland regional match',
    bounds: { minLat: 6.0, maxLat: 35.8, minLon: 68.0, maxLon: 97.5 },
    hints: ['india', 'indian freshwater', 'ganges', 'ganga', 'yamuna', 'brahmaputra', 'godavari', 'krishna river', 'narmada', 'cauvery', 'kaveri', 'reservoir', 'canal', 'pond'],
    waterTypes: ['lake', 'river', 'reservoir', 'pond', 'creek', 'canal', 'backwater'],
    priority: 73,
  },
  {
    id: 'india_coastal',
    label: 'India coastal',
    reasonLabel: 'India coastal regional match',
    bounds: { minLat: 6.0, maxLat: 24.5, minLon: 68.0, maxLon: 90.5 },
    hints: ['india coast', 'indian coast', 'arabian sea', 'bay of bengal', 'goa coast', 'mumbai coast', 'chennai coast', 'kerala coast', 'kerala backwaters', 'konkan', 'malabar coast', 'coromandel coast', 'sundarbans', 'andaman', 'nicobar'],
    waterTypes: ['bay', 'inshore', 'estuary', 'ocean', 'surf', 'marsh', 'reef', 'backwater'],
    priority: 72,
  },
  {
    id: 'japan_inshore',
    label: 'Japan inshore',
    reasonLabel: 'Japan regional match',
    bounds: { minLat: 24.0, maxLat: 46.5, minLon: 122.0, maxLon: 146.5 },
    hints: ['japan', 'tokyo bay', 'osaka bay', 'hokkaido', 'honshu', 'kyushu'],
    waterTypes: ['bay', 'inshore', 'estuary', 'ocean', 'surf'],
    priority: 62,
  },
  {
    id: 'korea_coastal',
    label: 'Korea coastal',
    reasonLabel: 'Korea regional match',
    bounds: { minLat: 33.0, maxLat: 39.6, minLon: 124.0, maxLon: 132.2 },
    hints: ['korea', 'south korea', 'jeju', 'busan', 'incheon', 'yellow sea', 'east sea'],
    waterTypes: ['bay', 'inshore', 'estuary', 'ocean', 'surf'],
    priority: 60,
  },
  {
    id: 'australia_estuary',
    label: 'Australia estuary/coast',
    reasonLabel: 'Australia regional match',
    bounds: { minLat: -44.0, maxLat: -10.0, minLon: 112.0, maxLon: 154.5 },
    hints: ['australia', 'queensland', 'new south wales', 'victoria', 'western australia', 'tasmania'],
    waterTypes: ['bay', 'inshore', 'estuary', 'ocean', 'surf', 'river'],
    priority: 58,
  },
];

export function detectFishIQRegionsForContext(ctx: FishIQRegionDetectionContext): FishIQRegionDefinition[] {
  const waterContext = classifyFishIQWaterContext(ctx);
  const explicitFreshwater = waterContext === 'freshwater' || waterContext === 'inland';
  const explicitCoastal = waterContext === 'coastal' || waterContext === 'offshore';
  const nearCoast = isNearFishIQCoastline(ctx);

  const direct = FISHIQ_REGION_DEFINITIONS
    .filter((def) => {
      const matched = inBounds(ctx, def) || hintMatches(ctx, def);
      if (!matched || !waterTypeFits(ctx, def)) return false;

      if (isCoastalRegion(def.id)) {
        if (explicitFreshwater) return false;
        return explicitCoastal || nearCoast;
      }

      return true;
    })
    .sort((a, b) => b.priority - a.priority);

  if (direct.length) return unique(direct, 4);

  if (explicitCoastal || (!explicitFreshwater && nearCoast)) {
    return [{
      id: 'generic_saltwater',
      label: 'Generic saltwater',
      reasonLabel: 'coastal/saltwater match',
      hints: ['saltwater', 'coastal'],
      priority: 5,
    }];
  }

  if (explicitFreshwater) {
    return [{
      id: 'generic_freshwater',
      label: 'Generic freshwater',
      reasonLabel: 'freshwater match',
      hints: ['freshwater', 'inland'],
      priority: 4,
    }];
  }

  return [];
}

export function getFishIQRegionIdsForContext(ctx: FishIQRegionDetectionContext): FishIQRegionId[] {
  return detectFishIQRegionsForContext(ctx).map((region) => region.id);
}

export function getFishIQRegionHintsForContext(ctx: FishIQRegionDetectionContext): string[] {
  const regions = detectFishIQRegionsForContext(ctx);
  return unique([
    ...regions.flatMap((region) => [region.label, region.reasonLabel, ...region.hints]),
    ...coordinateBroadHints(ctx),
  ], 16);
}

export function coordinateBroadHints(ctx: FishIQRegionDetectionContext): string[] {
  const lat = Number(ctx.lat);
  const lon = Number(ctx.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return [];

  const out: string[] = [];
  if (lat >= 24 && lat <= 72 && lon >= -170 && lon <= -52) out.push('North America');
  if (lat >= -56 && lat <= 13 && lon >= -82 && lon <= -34) out.push('South America');
  if (lat >= 34 && lat <= 72 && lon >= -25 && lon <= 45) out.push('Europe');
  if (lat >= -44 && lat <= -10 && lon >= 112 && lon <= 154.5) out.push('Australia');
  if (lat >= 1.12 && lat <= 1.55 && lon >= 103.55 && lon <= 104.15) out.push('Singapore', 'Southeast Asia', 'Indo-Pacific');
  if (lat >= 8.0 && lat <= 23.7 && lon >= 102.0 && lon <= 109.8) out.push('Vietnam', 'Southeast Asia', 'Indo-Pacific');
  if (lat >= 0.8 && lat <= 7.8 && lon >= 99.0 && lon <= 119.5) out.push('Malaysia', 'Southeast Asia', 'Indo-Pacific');
  if (lat >= 5.4 && lat <= 20.6 && lon >= 97.0 && lon <= 106.2) out.push('Thailand', 'Southeast Asia');
  if (lat >= 4.0 && lat <= 21.5 && lon >= 116.0 && lon <= 127.5) out.push('Philippines', 'Southeast Asia', 'Indo-Pacific');
  if (lat >= -11.5 && lat <= 6.5 && lon >= 94.0 && lon <= 142.5) out.push('Indonesia', 'Southeast Asia', 'Indo-Pacific');
  if (lat >= 6.0 && lat <= 35.8 && lon >= 68.0 && lon <= 97.5) out.push('India', 'South Asia', 'Indo-Pacific');
  if (lat >= 5 && lat <= 55 && lon >= 60 && lon <= 150) out.push('Asia');
  if (isNearFishIQCoastline(ctx)) {
    if (lon >= -125.5 && lon <= -116.85 && lat >= 32 && lat <= 49.2) out.push('Pacific Coast', 'West Coast');
    if (lon >= -98.8 && lon <= -81 && lat >= 25 && lat <= 31.6) out.push('Gulf Coast');
    if (lon >= -82 && lon <= -66 && lat >= 30 && lat <= 47.8) out.push('Atlantic Coast');
  }
  return unique(out, 10);
}
